/* eslint-disable @typescript-eslint/no-misused-promises */
/* @typescript-eslint/no-unsafe-argument */
import React, { FC, useEffect } from 'react';
import { Form, FormInstance, message } from 'antd';
import formatRUT from 'utils/rutFormatter';
import { validateRut } from '@abstract-cl/rut-ts';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  CreateLitePurchaseIntentInput,
  LitePurchaseIntent,
  PaymentMethod,
  PaymentMethodInput,
  AllowedCurrency,
  AllowedPaymentMethodType,
  RegistryRow,
} from 'apollo/types';
import operations from 'apollo/operations';
import { customerRut, isRutApiLoading } from 'apollo/cache';
import moment from 'moment';
import { PersonalInfo } from 'utils/apiService/types';
import apiService from 'utils/apiService';
import renderConcessionaireForm from './dynamicForms';

interface FormProps {
  form: FormInstance;
}

// eslint-disable-next-line
const DynamicForm: FC<FormProps> = (props: FormProps) => {
  const { form } = props;

  const [
    // eslint-disable-next-line
    createLitePurchaseIntent,
    { data: purchaseIntentData, loading: createPurchaseIntentLoading },
  ] = useMutation<
    { createLitePurchaseIntent: LitePurchaseIntent },
    { createLitePurchaseIntentInput: CreateLitePurchaseIntentInput }
  >(operations.CREATE_LITE_PURCHASE_INTENT);

  // eslint-disable-next-line
  const [createPaymentMethod, { data: paymentMethodData }] = useMutation<
    { createPaymentMethod: PaymentMethod },
    { paymentMethodInput: PaymentMethodInput }
  >(operations.POST_PAYMENT_METHOD);

  const handleCostumerAddress = (address: string) => {
    const adressArray = address.replace(/(\d+).*/, '$1');
    form.setFieldsValue({
      address: adressArray || '',
    });
  };

  const handleCostumerBirthday = (birthday: string) => {
    const birthDate = `${birthday.slice(0, 4)}-${birthday.slice(4, 6)}-${birthday.slice(
      6
    )}`;
    form.setFieldsValue({
      birthday: moment(birthDate, 'YYYY-MM-DD').format('DD-MM-YYYY') || '',
    });
  };

  const handleCustomerInfo = (personalInfo: PersonalInfo) => {
    const info = personalInfo?.personalInfo;
    const person = info.personaNatural;

    if (person?.nombreCompleto) {
      form.setFieldsValue({
        name: person?.nombreCompleto || '',
      });
    }
    if (info.direccion) {
      handleCostumerAddress(info?.direccion);
    }
    if (person?.fechaNac) {
      handleCostumerBirthday(person.fechaNac);
    }
  };
  const [getRegistryCommunesByName] = useLazyQuery<
    { registryCommunes: RegistryRow[] },
    { regionId: string; name: string }
  >(operations.GET_REGISTRY_COMMUNES_BY_NAME);

  const getClientInfo = async (rut: string) => {
    const response = await apiService.callRutApi({ rut });
    const commune = response.response?.personalInfo?.comuna;

    if (commune) {
      const communeResponse = await getRegistryCommunesByName({
        variables: {
          regionId: '1',
          name: commune.trimEnd(),
        },
      });
      if (communeResponse.data && communeResponse.data.registryCommunes.length > 0) {
        const communeData = communeResponse.data.registryCommunes[0];
        form.setFieldsValue({
          commune: `${communeData.id ?? ''}#${communeData.name}`,
          region: `${communeData.region_id ?? ''}#${communeData.name}`,
        });
      }
    }

    handleCustomerInfo(response.response);
    return response;
  };

  const handleChangeRut = async (rut: string): Promise<string> => {
    if (validateRut(rut) && rut.length >= 8) {
      const formatted: string = formatRUT(rut) || '';
      form.setFieldsValue({ rut: formatted });
      customerRut(formatted);
      isRutApiLoading(true);
      try {
        await getClientInfo(formatted);
      } catch (err) {
        message.warning(
          `No se encontraron datos para el rut, por favor rellenarlos manualmente`,
          3
        );
      }
      isRutApiLoading(false);
      return rut;
    }
    form.setFieldsValue({
      rut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const createPaymentMethodFunction = async () => {
    await createPaymentMethod({
      variables: {
        paymentMethodInput: {
          currency: AllowedCurrency.Clp,
          type: AllowedPaymentMethodType.WithFinancing,
        },
      },
    });
  };

  useEffect(() => {
    if (purchaseIntentData && purchaseIntentData.createLitePurchaseIntent) {
      localStorage.setItem(
        'purchaseIntentId',
        purchaseIntentData.createLitePurchaseIntent.purchaseIntentId
      );
      createPaymentMethodFunction();
    }
  }, [purchaseIntentData]);

  return (
    <Form form={form} name="carInformation" scrollToFirstError>
      {renderConcessionaireForm({
        createPurchaseIntentLoading,
        handleChangeRut,
        // setLegalEntity,
        // legalEntity,
        form,
      })}
    </Form>
  );
};

export default DynamicForm;
